import { hex } from "@scure/base";
import { sha256 } from "@noble/hashes/sha2.js";
import { schnorr, secp256k1 } from "@noble/curves/secp256k1.js";
import {
  Address,
  NETWORK,
  OutScript,
  Transaction,
  TaprootControlBlock,
  p2sh,
  p2tr,
  p2wpkh,
} from "@scure/btc-signer";
import {
  pubECDSA,
  pubSchnorr,
  randomPrivateKeyBytes,
  type BTC_NETWORK,
} from "@scure/btc-signer/utils.js";
import { OutOrdinalReveal, p2tr_ord_reveal, type Inscription } from "micro-ordinals";

import { burnScript, opReturnScript, propertyTags } from "./content";
import type {
  ConnectedWallet,
  InscriptionOutputManifestItem,
  InscriptionRareSatManifestItem,
  InscriptionSelectedSatManifestItem,
  InscriptionDraft,
  MempoolUtxo,
  OrdinalSource,
  RevealOptions,
  TransactionSummary,
  WalletAddress,
} from "./types";
import { normalizeXOnlyPublicKey } from "./wallets";

export const DEFAULT_POSTAGE = 330;
export const MIN_FEE_RATE = 0.1;
export const MAX_STANDARD_TX_WEIGHT = 400_000;
export const MAX_VANITY_NLOCKTIME = 5_000_000;

const BASE_TX_VBYTES = 10.5;
const INPUT_VBYTES: Record<"p2sh" | "p2wpkh" | "p2tr", number> = {
  p2sh: 91,
  p2wpkh: 68,
  p2tr: 58,
};
const DUMMY_PRIVATE_KEY = hex.decode("0a".repeat(32));
const CUSTOM_SCRIPTS = [OutOrdinalReveal];
const HEX_FRAGMENT = /^[0-9a-f]{0,5}$/i;
const INSCRIPTION_ID_PATTERN = /^[0-9a-f]{64}i[0-9]{1,10}$/i;

type PaymentKind = keyof typeof INPUT_VBYTES;

interface PaymentDetails {
  kind: PaymentKind;
  script: Uint8Array;
  redeemScript?: Uint8Array;
  witnessScript?: Uint8Array;
  tapInternalKey?: Uint8Array;
}

export interface InscriptionPlan {
  commit: Transaction;
  revealPayment: ReturnType<typeof p2tr>;
  inscriptionObjects: Inscription[];
  selectedUtxos: MempoolUtxo[];
  sourceSources: OrdinalSource[];
  revealAmount: number;
  revealCommitAmounts: number[];
  alignmentOutputAmount: number;
  outputPostages: number[];
  recipients: string[];
  parentSources: OrdinalSource[];
  parentIds: string[];
  revealInputIndex: number;
  revealOptions: RevealOptions;
  ephemeralPrivateKey?: Uint8Array;
  summary: TransactionSummary;
}

export interface CreatePlanOptions {
  wallet: ConnectedWallet;
  drafts: InscriptionDraft[];
  utxos: MempoolUtxo[];
  commitFeeRate: number;
  revealFeeRate: number;
  marketplaceFeeAddress: string;
  marketplaceFeeSats: number;
  additionalPayments?: Array<{ address: string; amountSats: number }>;
  revealOptions?: RevealOptions;
  allowTaprootPayment?: boolean;
  commitInputSequence?: number;
  /** Server-only deterministic reveal key for resumable custodial batches. */
  ephemeralRevealPrivateKey?: Uint8Array;
  network?: BTC_NETWORK;
}

export interface InscriptionFundingPlan {
  commit: Transaction;
  selectedUtxos: MempoolUtxo[];
  paymentInputIndexes: number[];
  commitFee: number;
  commitVsize: number;
  total: number;
}

export interface CreateInscriptionFundingPlanOptions {
  payment: WalletAddress;
  utxos: MempoolUtxo[];
  feeRate: number;
  revealAddress: string;
  revealAmountSats: number;
  marketplaceFeeAddress: string;
  marketplaceFeeSats: number;
  creatorPaymentAddress: string;
  creatorPaymentSats: number;
  creatorPayments?: Array<{ address: string; amountSats: number }>;
  network?: BTC_NETWORK;
}

export interface ServerInscriptionRevealPlan {
  revealPayment: ReturnType<typeof p2tr>;
  inscription: Inscription;
  recipient: string;
  postageSats: number;
  opReturnScript?: Uint8Array;
  revealAmountSats: number;
  revealFeeSats: number;
  revealVsize: number;
  inputSequence: number;
}

function paymentKind(address: string): PaymentKind {
  if (address.startsWith("3")) return "p2sh";
  if (address.startsWith("bc1q")) return "p2wpkh";
  if (address.startsWith("bc1p")) return "p2tr";
  throw new Error("Only nested SegWit, native SegWit, and Taproot payment addresses are supported");
}

function paymentDetails(address: WalletAddress, network: BTC_NETWORK): PaymentDetails {
  const kind = paymentKind(address.address);
  const publicKeyBytes = hex.decode(address.publicKey);

  if (kind === "p2tr") {
    const internalKey = publicKeyBytes.length === 33 ? publicKeyBytes.slice(1) : publicKeyBytes;
    const payment = p2tr(internalKey, undefined, network);
    if (payment.address !== address.address) throw new Error("Wallet Taproot public key does not match its payment address");
    return { kind, script: payment.script, tapInternalKey: internalKey };
  }

  if (publicKeyBytes.length !== 33) throw new Error("Wallet returned an unsupported SegWit public key");
  const native = p2wpkh(publicKeyBytes, network);
  if (kind === "p2wpkh") {
    if (native.address !== address.address) throw new Error("Wallet public key does not match its payment address");
    return { kind, script: native.script };
  }

  const nested = p2sh(native, network);
  if (nested.address !== address.address) throw new Error("Wallet public key does not match its payment address");
  return { kind, script: nested.script, redeemScript: nested.redeemScript };
}

function outputVbytes(address: string, network: BTC_NETWORK): number {
  const script = OutScript.encode(Address(network).decode(address));
  return 8 + 1 + script.length;
}

function parseOutpoint(outpoint: string): { txid: string; index: number } {
  const [txid, indexValue] = outpoint.split(":");
  const index = Number(indexValue);
  if (!/^[0-9a-f]{64}$/i.test(txid ?? "") || !Number.isSafeInteger(index) || index < 0) {
    throw new Error(`Invalid ordinal outpoint: ${outpoint}`);
  }
  return { txid: txid ?? "", index };
}

function draftToInscription(draft: InscriptionDraft, index: number, pointer: number, selectedParentIds: string[] = []): Inscription {
  const tags: Record<string, unknown> = { pointer: BigInt(pointer) };
  const body = draft.body ?? new Uint8Array();

  if (body.length > 0) {
    if (!draft.mimeType) throw new Error(`${draft.name} is missing a content type`);
    tags.contentType = draft.mimeType;
    if (draft.contentEncoding) tags.contentEncoding = draft.contentEncoding;
  } else if (draft.delegateId) {
    tags.delegate = draft.delegateId;
  } else if (draft.rune === undefined) {
    throw new Error(`Inscription ${index + 1} has no file, text, delegate, or Rune commitment`);
  }
  if (draft.rune !== undefined) {
    if (draft.rune < 0n || draft.rune > (1n << 128n) - 1n) throw new Error(`Inscription ${index + 1} has an invalid Rune commitment`);
    tags.rune = draft.rune;
  }

  if (selectedParentIds.length) tags.parent = selectedParentIds;
  if (draft.metadata !== undefined) tags.metadata = draft.metadata;
  if (draft.metaprotocol?.trim()) tags.metaprotocol = draft.metaprotocol.trim();
  const unknown = propertyTags(draft.attributes, draft.gallery);
  if (unknown) tags.unknown = unknown;
  return { tags, body };
}

function buildInscriptionObjects(
  drafts: InscriptionDraft[],
  postages: number[],
  pointerStart = 0,
  selectedParentIds: string[] = [],
): Inscription[] {
  let pointer = pointerStart;
  return drafts.map((draft, index) => {
    const inscription = draftToInscription(draft, index, pointer, draft.parentIds ?? selectedParentIds);
    pointer += postages[index] ?? DEFAULT_POSTAGE;
    return inscription;
  });
}

function buildAlignedInscriptionObjects(
  drafts: InscriptionDraft[],
  postages: number[],
  pointerStart: number,
  selectedParentIds: string[] = [],
): Inscription[] {
  return buildInscriptionObjects(drafts, postages, pointerStart, selectedParentIds);
}

function addRevealOutputs(
  transaction: Transaction,
  options: RevealOptions,
  parents: OrdinalSource[],
  recipients: string[],
  postages: number[],
  ordinalAddress: string,
  network: BTC_NETWORK,
  alignmentOutputAmount = 0,
): void {
  if (options.opReturnData?.trim() && options.opReturnScript) throw new Error("Choose either text or a binary OP_RETURN output");
  if (options.opReturnData?.trim()) transaction.addOutput({ script: opReturnScript(options.opReturnData.trim()), amount: 0n });
  if (options.opReturnScript) transaction.addOutput({ script: options.opReturnScript, amount: 0n });
  parents.forEach((parent) => transaction.addOutputAddress(ordinalAddress, BigInt(parent.value), network));
  if (alignmentOutputAmount > 0) transaction.addOutputAddress(ordinalAddress, BigInt(alignmentOutputAmount), network);
  if (options.burn) {
    if (recipients.length !== 1) throw new Error("Burn on inscription is available only for a single inscription");
    transaction.addOutput({ script: burnScript(options.burnMessage), amount: 1n });
  } else {
    recipients.forEach((recipient, index) => {
      transaction.addOutputAddress(recipient, BigInt(postages[index] ?? DEFAULT_POSTAGE), network);
    });
  }
  for (const output of options.additionalOutputs ?? []) {
    transaction.addOutputAddress(output.address, BigInt(output.amountSats), network);
  }
}

function estimateRevealVsize(
  inscriptions: Inscription[],
  recipients: string[],
  postages: number[],
  parents: OrdinalSource[],
  sources: OrdinalSource[],
  options: RevealOptions,
  network: BTC_NETWORK,
  revealCommitAmounts: number[],
  alignmentOutputAmount: number,
): number {
  const dummyPubKey = pubSchnorr(DUMMY_PRIVATE_KEY);
  const parentPayment = p2tr(dummyPubKey, undefined, network);
  const revealPayment = p2tr(dummyPubKey, p2tr_ord_reveal(dummyPubKey, inscriptions), network, false, CUSTOM_SCRIPTS);
  const tx = new Transaction({ customScripts: CUSTOM_SCRIPTS, allowUnknownOutputs: true, disableScriptCheck: true });
  let dummyInputIndex = 1;
  const addOrdinalInput = (source: Pick<OrdinalSource, "value">) => tx.addInput({
    ...parentPayment,
    txid: (dummyInputIndex++).toString(16).padStart(64, "0"),
    index: 0,
    witnessUtxo: { script: parentPayment.script, amount: BigInt(source.value) },
    sequence: 0xfffffffd,
  });
  parents.forEach(addOrdinalInput);
  if (alignmentOutputAmount > 0) {
    sources.forEach((source, index) => {
      addOrdinalInput({ value: revealCommitAmounts[index] ?? DEFAULT_POSTAGE });
      addOrdinalInput(source);
    });
  } else {
    sources.forEach(addOrdinalInput);
  }
  tx.addInput({
    ...revealPayment,
    txid: "75ddabb27b8845f5247975c8a5ba7c6f336c4570708ebe230caf6db5217ae858",
    index: 0,
    witnessUtxo: { script: revealPayment.script, amount: 1_000_000_000n },
    sequence: 0xfffffffd,
  });
  addRevealOutputs(tx, options, parents, recipients, postages, parentPayment.address ?? recipients[0], network, alignmentOutputAmount);
  tx.sign(DUMMY_PRIVATE_KEY, undefined, new Uint8Array(32));
  const revealIndex = tx.inputsLength - 1;
  if (tx.getInput(revealIndex).tapKeySig) tx.updateInput(revealIndex, { tapKeySig: undefined }, true);
  tx.finalize();
  if (tx.weight > MAX_STANDARD_TX_WEIGHT) throw new Error("This inscription package is larger than the standard Bitcoin transaction limit");
  return tx.vsize + 2;
}

function selectUtxos(
  utxos: MempoolUtxo[],
  fixedSpend: number,
  feeRate: number,
  paymentInputVbytes: number,
  outputsVbytes: number,
): { selected: MempoolUtxo[]; total: number; commitFee: number; commitVsize: number; change: number } {
  const sorted = [...utxos].filter((utxo) => utxo.status.confirmed).sort((left, right) => right.value - left.value);
  const selected: MempoolUtxo[] = [];
  let total = 0;

  for (let attempt = 0; attempt <= sorted.length; attempt += 1) {
    const commitVsize = Math.ceil(BASE_TX_VBYTES + outputsVbytes + paymentInputVbytes * selected.length);
    const commitFee = Math.ceil(commitVsize * feeRate);
    if (total >= fixedSpend + commitFee) {
      return { selected, total, commitFee, commitVsize, change: total - fixedSpend - commitFee };
    }
    const next = sorted[attempt];
    if (!next) break;
    selected.push(next);
    total += next.value;
  }
  throw new Error("Not enough confirmed BTC for this inscription");
}

function validateVanity(options: RevealOptions): void {
  const start = options.vanityStart?.trim() ?? "";
  const end = options.vanityEnd?.trim() ?? "";
  if (!HEX_FRAGMENT.test(start) || !HEX_FRAGMENT.test(end)) throw new Error("Vanity fragments must contain no more than five hexadecimal characters");
  if (start.length + end.length > 5) throw new Error("Use no more than five vanity characters across the beginning and end");
}

export function createInscriptionFundingPlan(
  options: CreateInscriptionFundingPlanOptions,
): InscriptionFundingPlan {
  const network = options.network ?? NETWORK;
  if (!Number.isFinite(options.feeRate) || options.feeRate < MIN_FEE_RATE || options.feeRate > 1_000) {
    throw new Error(`Fee rate must be between ${MIN_FEE_RATE} and 1,000 sat/vB`);
  }
  if (!Number.isSafeInteger(options.revealAmountSats) || options.revealAmountSats < DEFAULT_POSTAGE) {
    throw new Error(`Reveal funding must be at least ${DEFAULT_POSTAGE} sats`);
  }
  if (!Number.isSafeInteger(options.marketplaceFeeSats) || options.marketplaceFeeSats < 0) {
    throw new Error("Invalid marketplace fee");
  }
  if (!Number.isSafeInteger(options.creatorPaymentSats) || options.creatorPaymentSats < 0) {
    throw new Error("Invalid creator payment");
  }
  Address(network).decode(options.revealAddress);
  if (options.marketplaceFeeSats > 0) Address(network).decode(options.marketplaceFeeAddress);
  const creatorPayments = (options.creatorPayments ?? (
    options.creatorPaymentSats > 0
      ? [{ address: options.creatorPaymentAddress, amountSats: options.creatorPaymentSats }]
      : []
  )).filter((payment) => payment.amountSats > 0);
  if (creatorPayments.some((payment) => !Address(network).decode(payment.address) || !Number.isSafeInteger(payment.amountSats) || payment.amountSats < DEFAULT_POSTAGE)) {
    throw new Error(`Creator payments must be at least ${DEFAULT_POSTAGE} sats`);
  }

  const payment = paymentDetails(options.payment, network);
  if (payment.kind === "p2tr") {
    throw new Error("Inscription payments must come from a SegWit payment address");
  }
  const creatorPaymentTotal = creatorPayments.reduce((sum, payment) => sum + payment.amountSats, 0);
  const fixedSpend = options.revealAmountSats + options.marketplaceFeeSats + creatorPaymentTotal;
  const outputsVbytes = outputVbytes(options.revealAddress, network)
    + outputVbytes(options.payment.address, network)
    + (options.marketplaceFeeSats > 0 ? outputVbytes(options.marketplaceFeeAddress, network) : 0)
    + creatorPayments.reduce((sum, creatorPayment) => sum + outputVbytes(creatorPayment.address, network), 0);
  const selected = selectUtxos(
    options.utxos,
    fixedSpend,
    options.feeRate,
    INPUT_VBYTES[payment.kind],
    outputsVbytes,
  );
  const commit = new Transaction({ allowUnknownOutputs: true });
  for (const utxo of selected.selected) {
    commit.addInput({
      txid: utxo.txid,
      index: utxo.vout,
      witnessUtxo: { script: payment.script, amount: BigInt(utxo.value) },
      redeemScript: payment.redeemScript,
      witnessScript: payment.witnessScript,
      tapInternalKey: payment.tapInternalKey,
      // The server reveals immediately after accepting this exact parent. Do not
      // make the authorized parent replaceable underneath its child.
      sequence: 0xffffffff,
    });
  }
  commit.addOutputAddress(options.revealAddress, BigInt(options.revealAmountSats), network);
  if (options.marketplaceFeeSats > 0) {
    commit.addOutputAddress(options.marketplaceFeeAddress, BigInt(options.marketplaceFeeSats), network);
  }
  creatorPayments.forEach((creatorPayment) => commit.addOutputAddress(creatorPayment.address, BigInt(creatorPayment.amountSats), network));
  if (selected.change >= DEFAULT_POSTAGE) {
    commit.addOutputAddress(options.payment.address, BigInt(selected.change), network);
  }
  const commitFee = Number(commit.fee);
  return {
    commit,
    selectedUtxos: selected.selected,
    paymentInputIndexes: selected.selected.map((_, index) => index),
    commitFee,
    commitVsize: selected.commitVsize,
    total: fixedSpend + commitFee,
  };
}

export function createServerInscriptionRevealPlan(options: {
  privateKey: Uint8Array;
  body: Uint8Array;
  contentType: string;
  recipient: string;
  feeRate: number;
  postageSats?: number;
  opReturnScript?: Uint8Array;
  inputSequence?: number;
  network?: BTC_NETWORK;
}): ServerInscriptionRevealPlan {
  const network = options.network ?? NETWORK;
  const postageSats = options.postageSats ?? DEFAULT_POSTAGE;
  if (options.privateKey.length !== 32) throw new Error("Reveal key must contain 32 bytes");
  if (!options.body.length) throw new Error("The inscription file is empty");
  if (!options.contentType.trim()) throw new Error("The inscription content type is missing");
  if (!options.recipient.startsWith("bc1p")) throw new Error("The inscription destination must be a Taproot address");
  if (!Number.isFinite(options.feeRate) || options.feeRate < MIN_FEE_RATE || options.feeRate > 1_000) {
    throw new Error(`Fee rate must be between ${MIN_FEE_RATE} and 1,000 sat/vB`);
  }
  if (!Number.isSafeInteger(postageSats) || postageSats < DEFAULT_POSTAGE) {
    throw new Error(`Postage must be at least ${DEFAULT_POSTAGE} sats`);
  }
  const inputSequence = options.inputSequence ?? 0xfffffffd;
  if (!Number.isSafeInteger(inputSequence) || inputSequence < 0 || inputSequence > 0xffffffff) {
    throw new Error("Reveal input sequence is invalid");
  }
  if (options.opReturnScript && (
    options.opReturnScript.length < 2
    || options.opReturnScript.length > 83
    || options.opReturnScript[0] !== 0x6a
  )) throw new Error("The server reveal OP_RETURN script is invalid");
  Address(network).decode(options.recipient);
  const inscription = draftToInscription({
    id: "server-reveal",
    name: "server-reveal",
    mimeType: options.contentType,
    body: options.body,
  }, 0, 0);
  const revealKey = pubSchnorr(options.privateKey);
  const revealPayment = p2tr(
    revealKey,
    p2tr_ord_reveal(revealKey, [inscription]),
    network,
    false,
    CUSTOM_SCRIPTS,
  );
  const revealVsize = estimateRevealVsize(
    [inscription],
    [options.recipient],
    [postageSats],
    [],
    [],
    { opReturnScript: options.opReturnScript },
    network,
    [postageSats],
    0,
  );
  const revealFeeSats = Math.ceil(revealVsize * options.feeRate);
  return {
    revealPayment,
    inscription,
    recipient: options.recipient,
    postageSats,
    opReturnScript: options.opReturnScript ? Uint8Array.from(options.opReturnScript) : undefined,
    revealAmountSats: postageSats + revealFeeSats,
    revealFeeSats,
    revealVsize,
    inputSequence,
  };
}

export function buildAndSignServerRevealTransaction(
  plan: ServerInscriptionRevealPlan,
  privateKey: Uint8Array,
  commitTxid: string,
  commitOutputIndex = 0,
  network: BTC_NETWORK = NETWORK,
): Transaction {
  if (!/^[0-9a-f]{64}$/i.test(commitTxid)) throw new Error("Invalid commit transaction ID");
  if (!Number.isSafeInteger(commitOutputIndex) || commitOutputIndex < 0) throw new Error("Invalid reveal output index");
  const transaction = new Transaction({
    version: 2,
    customScripts: CUSTOM_SCRIPTS,
    allowUnknownOutputs: true,
    disableScriptCheck: true,
  });
  transaction.addInput({
    ...plan.revealPayment,
    txid: commitTxid,
    index: commitOutputIndex,
    witnessUtxo: { script: plan.revealPayment.script, amount: BigInt(plan.revealAmountSats) },
    sequence: plan.inputSequence,
  });
  transaction.addOutputAddress(plan.recipient, BigInt(plan.postageSats), network);
  if (plan.opReturnScript) transaction.addOutput({ script: plan.opReturnScript, amount: 0n });
  transaction.sign(privateKey, undefined, new Uint8Array(32));
  if (transaction.getInput(0).tapKeySig) transaction.updateInput(0, { tapKeySig: undefined }, true);
  transaction.finalize();
  if (transaction.weight > MAX_STANDARD_TX_WEIGHT) {
    throw new Error("Signed reveal transaction exceeds the standard weight limit");
  }
  return transaction;
}

export function createInscriptionPlan(options: CreatePlanOptions): InscriptionPlan {
  const network = options.network ?? NETWORK;
  const revealOptions = options.revealOptions ?? {};
  const additionalRevealOutputs = revealOptions.additionalOutputs ?? [];
  if (revealOptions.source && revealOptions.sources?.length) throw new Error("Choose either one source or a batch of sat sources");
  const sourceItems = revealOptions.sources?.length ? revealOptions.sources : revealOptions.source ? [revealOptions.source] : [];
  if (options.drafts.length === 0) throw new Error("Add at least one inscription");
  if (revealOptions.burn && options.drafts.some((draft) => draft.delegateId)) {
    throw new Error("Delegate inscriptions cannot be combined with Burn here. Use the dedicated Burn tool instead");
  }
  if (!options.wallet.ordinals.address.startsWith("bc1p")) throw new Error("A Taproot ordinals address is required");
  if (!options.marketplaceFeeAddress) throw new Error("The marketplace fee wallet has not been configured");
  if (additionalRevealOutputs.some((output) => !Number.isSafeInteger(output.amountSats) || output.amountSats < DEFAULT_POSTAGE)) {
    throw new Error(`Additional reveal outputs must be at least ${DEFAULT_POSTAGE} sats`);
  }
  try {
    additionalRevealOutputs.forEach((output) => Address(network).decode(output.address));
  } catch {
    throw new Error("Additional reveal outputs need valid Bitcoin mainnet addresses");
  }
  if (revealOptions.inputSequence !== undefined && (!Number.isSafeInteger(revealOptions.inputSequence) || revealOptions.inputSequence < 0 || revealOptions.inputSequence > 0xffffffff)) {
    throw new Error("Reveal input sequence is invalid");
  }
  if (options.commitInputSequence !== undefined && (!Number.isSafeInteger(options.commitInputSequence) || options.commitInputSequence < 0 || options.commitInputSequence > 0xffffffff)) {
    throw new Error("Commit input sequence is invalid");
  }
  const additionalPayments = (options.additionalPayments ?? []).filter((payment) => payment.amountSats > 0);
  if (additionalPayments.some((payment) => !Address(network).decode(payment.address) || !Number.isSafeInteger(payment.amountSats) || payment.amountSats < DEFAULT_POSTAGE)) {
    throw new Error(`Additional Bitcoin payments must be at least ${DEFAULT_POSTAGE} sats`);
  }
  if (options.commitFeeRate < MIN_FEE_RATE || options.revealFeeRate < MIN_FEE_RATE) throw new Error(`Fee rate must be at least ${MIN_FEE_RATE} sat/vB`);
  if (revealOptions.source && options.drafts.length !== 1) throw new Error("Specific-sat and reinscription sources require a single inscription");
  if (sourceItems.length > options.drafts.length) throw new Error("Select no more than one sat source per inscription");
  if (new Set(sourceItems.map((source) => source.outpoint)).size !== sourceItems.length) throw new Error("Each sat source can only be used once");
  if (sourceItems.some((source) => source.address !== options.wallet.ordinals.address)) throw new Error("Every sat source must belong to the connected Ordinals wallet");
  if (sourceItems.some((source) => !Number.isSafeInteger(source.satOffset ?? 0) || (source.satOffset ?? 0) < 0 || (source.satOffset ?? 0) >= source.value)) {
    throw new Error("A selected sat has an invalid position in its UTXO");
  }
  if (sourceItems.some((source) => (source.satOffset ?? 0) !== 0) && !(
    options.drafts.length === 1
    && Boolean(options.drafts[0]?.gallery?.length)
    && sourceItems.length === 1
    && sourceItems[0]?.selectionKind === "specific"
  )) {
    throw new Error("Only the first sat of a UTXO can be used for inscription");
  }
  const requestedParents = [revealOptions.parent, ...(revealOptions.coParents ?? [])].filter((parent): parent is OrdinalSource => Boolean(parent));
  const requestedParentIds = requestedParents.map((parent) => {
    if (!parent.inscriptionId) throw new Error("Every selected parent is missing its inscription ID");
    return parent.inscriptionId;
  });
  const sourceParentIds = sourceItems.flatMap((source) => {
    if (!source.parentRelation) return [];
    if (source.selectionKind !== "specific" || !source.inscriptionId) {
      throw new Error("A selected sat can be encoded as a parent only when an existing inscription was resolved on it");
    }
    return [source.inscriptionId];
  });
  const combinedParentIds = [...requestedParentIds, ...sourceParentIds];
  if (new Set(combinedParentIds.map((id) => id.toLowerCase())).size !== combinedParentIds.length) {
    throw new Error("Each parent inscription can only be selected once");
  }
  const parentIds = [...new Set(combinedParentIds.map((id) => id.toLowerCase()))];
  const usesPerDraftParents = options.drafts.some((draft) => draft.parentIds !== undefined);
  if (usesPerDraftParents) {
    const declared = options.drafts.flatMap((draft, draftIndex) => {
      const ids = draft.parentIds ?? [];
      const normalized = ids.map((id) => id.toLowerCase());
      if (normalized.some((id) => !INSCRIPTION_ID_PATTERN.test(id))) {
        throw new Error(`Inscription ${draftIndex + 1} has an invalid parent inscription ID`);
      }
      if (new Set(normalized).size !== normalized.length) {
        throw new Error(`Inscription ${draftIndex + 1} declares the same parent more than once`);
      }
      return normalized;
    });
    const declaredSet = new Set(declared);
    const requestedSet = new Set(requestedParentIds.map((id) => id.toLowerCase()));
    if (declaredSet.size !== requestedSet.size || [...declaredSet].some((id) => !requestedSet.has(id))) {
      throw new Error("Every per-inscription parent must match one selected parent input");
    }
  }
  if (requestedParents.some((parent) => parent.address !== options.wallet.ordinals.address)) {
    throw new Error("Every parent must belong to the connected Ordinals wallet");
  }
  if (sourceItems.some((source) => requestedParents.some((parent) => parent.outpoint === source.outpoint))) {
    throw new Error("The parent inscription cannot also be used as the inscription source");
  }
  if (revealOptions.transactionVersion === 3) throw new Error("V3 reveal transactions are not active on Bitcoin mainnet");
  validateVanity(revealOptions);

  const ordinalsKey = normalizeXOnlyPublicKey(options.wallet.ordinals.publicKey);
  const ordinalPayment = paymentDetails(options.wallet.ordinals, network);
  if (ordinalPayment.kind !== "p2tr") throw new Error("Ordinal inputs require a Taproot wallet address");
  const parentSources = requestedParents.filter((parent, index) => requestedParents.findIndex((candidate) => candidate.outpoint === parent.outpoint) === index);
  const sourceOffsets = sourceItems.map((source) => source.satOffset ?? 0);
  const alignedSatOutputs = sourceOffsets.some((offset) => offset > 0);
  if (alignedSatOutputs && revealOptions.burn) throw new Error("Burn cannot be combined with an internal selected sat");
  let alignmentOutputAmount = 0;
  let revealCommitAmounts = [DEFAULT_POSTAGE];
  const postages = options.drafts.map((draft, index) => {
    const source = sourceItems[index];
    if (!source) return draft.postage ?? DEFAULT_POSTAGE;
    if (!alignedSatOutputs) return source.value;
    const offset = sourceOffsets[index] ?? 0;
    const nextOffset = sourceOffsets[index + 1] ?? 0;
    return source.value - offset + DEFAULT_POSTAGE + nextOffset;
  });
  if (alignedSatOutputs) {
    alignmentOutputAmount = DEFAULT_POSTAGE + (sourceOffsets[0] ?? 0);
    revealCommitAmounts = Array.from({ length: sourceItems.length + 1 }, () => DEFAULT_POSTAGE);
  }
  const recipients = options.drafts.map((draft) => draft.recipient || options.wallet.ordinals.address);
  if (postages.some((value) => !Number.isSafeInteger(value) || value < DEFAULT_POSTAGE)) {
    throw new Error(`Postage must be a whole number of at least ${DEFAULT_POSTAGE} sats`);
  }
  if (recipients.some((address) => !address.startsWith("bc1p"))) {
    throw new Error("Inscription recipients must be Bitcoin mainnet Taproot addresses (bc1p…)");
  }
  try {
    recipients.forEach((address) => Address(network).decode(address));
  } catch {
    throw new Error("Inscription recipients must be valid Bitcoin mainnet Taproot addresses (bc1p…)");
  }
  const parentOutputValue = parentSources.reduce((sum, parent) => sum + parent.value, 0);
  const inscriptionObjects = alignedSatOutputs
    ? buildAlignedInscriptionObjects(options.drafts, postages, parentOutputValue + alignmentOutputAmount, parentIds)
    : buildInscriptionObjects(options.drafts, postages, parentOutputValue, parentIds);
  const ephemeralPrivateKey = revealOptions.ephemeralReveal
    ? options.ephemeralRevealPrivateKey ?? randomPrivateKeyBytes()
    : undefined;
  if (ephemeralPrivateKey && (ephemeralPrivateKey.length !== 32 || !secp256k1.utils.isValidSecretKey(ephemeralPrivateKey))) {
    throw new Error("Ephemeral reveal key is invalid");
  }
  const revealScriptKey = ephemeralPrivateKey ? pubSchnorr(ephemeralPrivateKey) : ordinalsKey;
  const revealPayment = p2tr(ordinalsKey, p2tr_ord_reveal(revealScriptKey, inscriptionObjects), network, false, CUSTOM_SCRIPTS);
  const revealVsize = estimateRevealVsize(
    inscriptionObjects,
    recipients,
    postages,
    parentSources,
    sourceItems,
    revealOptions,
    network,
    revealCommitAmounts,
    alignmentOutputAmount,
  );
  const estimatedRevealFee = Math.ceil(revealVsize * options.revealFeeRate);
  const postage = postages.reduce((sum, value) => sum + value, 0);
  const additionalRevealPostage = additionalRevealOutputs.reduce((sum, output) => sum + output.amountSats, 0);
  const sourceValue = sourceItems.reduce((sum, source) => sum + source.value, 0);
  const fundedPostage = postage - sourceValue;
  let revealFee = estimatedRevealFee;
  if (alignedSatOutputs) {
    const ordinaryPostage = postages.slice(sourceItems.length).reduce((sum, value) => sum + value, 0);
    revealCommitAmounts[revealCommitAmounts.length - 1] = DEFAULT_POSTAGE + ordinaryPostage + revealFee;
  } else {
    const revealAmount = Math.max(DEFAULT_POSTAGE, fundedPostage + additionalRevealPostage + estimatedRevealFee);
    revealCommitAmounts = [revealAmount];
    revealFee = revealAmount - fundedPostage - additionalRevealPostage;
  }
  const revealAmount = revealCommitAmounts.reduce((sum, value) => sum + value, 0);
  const marketplaceFee = options.marketplaceFeeSats * options.drafts.length;
  const additionalPaymentTotal = additionalPayments.reduce((sum, payment) => sum + payment.amountSats, 0);
  const payment = paymentDetails(options.wallet.payment, network);
  if (payment.kind === "p2tr" && !options.allowTaprootPayment) {
    throw new Error("Inscription payments must come from a SegWit payment address");
  }
  const outputsVbytes = outputVbytes(options.wallet.payment.address, network)
    + (marketplaceFee > 0 ? outputVbytes(options.marketplaceFeeAddress, network) : 0)
    + additionalPayments.reduce((sum, payment) => sum + outputVbytes(payment.address, network), 0)
    + 43 * revealCommitAmounts.length;
  const selected = selectUtxos(
    options.utxos,
    revealAmount + marketplaceFee + additionalPaymentTotal,
    options.commitFeeRate,
    INPUT_VBYTES[payment.kind],
    outputsVbytes,
  );

  const commit = new Transaction({ allowUnknownOutputs: true });
  for (const utxo of selected.selected) {
    commit.addInput({
      txid: utxo.txid,
      index: utxo.vout,
      witnessUtxo: { script: payment.script, amount: BigInt(utxo.value) },
      redeemScript: payment.redeemScript,
      witnessScript: payment.witnessScript,
      tapInternalKey: payment.tapInternalKey,
      sequence: options.commitInputSequence ?? 0xfffffffd,
    });
  }
  if (!revealPayment.address) throw new Error("Could not derive the reveal address");
  revealCommitAmounts.forEach((amount) => commit.addOutputAddress(revealPayment.address ?? "", BigInt(amount), network));
  if (marketplaceFee > 0) commit.addOutputAddress(options.marketplaceFeeAddress, BigInt(marketplaceFee), network);
  additionalPayments.forEach((additionalPayment) => commit.addOutputAddress(additionalPayment.address, BigInt(additionalPayment.amountSats), network));
  if (selected.change >= DEFAULT_POSTAGE) commit.addOutputAddress(options.wallet.payment.address, BigInt(selected.change), network);

  const actualCommitFee = Number(commit.fee);
  const summary: TransactionSummary = {
    count: options.drafts.length,
    networkFee: actualCommitFee + revealFee,
    marketplaceFee,
    additionalPayments: additionalPaymentTotal,
    postage: postage + additionalRevealPostage,
    total: actualCommitFee + revealFee + marketplaceFee + additionalPaymentTotal + postage + additionalRevealPostage,
    commitFee: actualCommitFee,
    revealFee,
    commitVsize: selected.commitVsize,
    revealVsize,
  };

  return {
    commit,
    revealPayment,
    inscriptionObjects,
    selectedUtxos: selected.selected,
    sourceSources: sourceItems,
    revealAmount,
    revealCommitAmounts,
    alignmentOutputAmount,
    outputPostages: postages,
    recipients,
    parentSources,
    parentIds,
    revealInputIndex: parentSources.length + sourceItems.length + (alignedSatOutputs ? sourceItems.length : 0),
    revealOptions,
    ephemeralPrivateKey,
    summary,
  };
}

export function inscriptionOutputManifestForPlan(plan: InscriptionPlan): InscriptionOutputManifestItem[] | undefined {
  if (plan.revealOptions.burn || plan.inscriptionObjects.length === 0) return undefined;
  const delegates = plan.inscriptionObjects.map((inscription) => (
    inscription.tags as unknown as Record<string, unknown>
  ).delegate);
  return plan.inscriptionObjects.map((inscription, index) => {
    const tags = inscription.tags as unknown as Record<string, unknown>;
    const rawParents = tags.parent;
    const parentIds = rawParents === undefined ? [] : (Array.isArray(rawParents) ? rawParents : [rawParents]);
    return {
      index,
      recipient: plan.recipients[index] ?? "",
      postageSats: plan.outputPostages[index] ?? DEFAULT_POSTAGE,
      ...(typeof delegates[index] === "string" ? { delegateId: delegates[index] } : {}),
      ...(typeof tags.contentType === "string" ? { contentType: tags.contentType } : {}),
      contentLength: inscription.body.length,
      contentSha256: hex.encode(sha256(inscription.body)),
      parentIds: parentIds as string[],
    };
  });
}

export function inscriptionRareSatManifestForPlan(plan: InscriptionPlan): InscriptionRareSatManifestItem[] | undefined {
  return inscriptionSelectedSatManifestForPlan(plan);
}

export function inscriptionSelectedSatManifestForPlan(plan: InscriptionPlan): InscriptionSelectedSatManifestItem[] | undefined {
  const selections = plan.sourceSources.flatMap((source, index) => {
    if (!Number.isSafeInteger(source.sat) || Number(source.sat) < 0) return [];
    const satOffset = source.satOffset ?? 0;
    const kind = source.selectionKind ?? "rare";
    return [{
      index,
      outpoint: source.outpoint,
      sat: Number(source.sat),
      satOffset,
      value: source.value,
      ...(kind === "specific" ? { kind } : {}),
      ...(kind === "specific" ? {
        referenceType: source.referenceType,
        satpoint: source.satpoint ?? `${source.outpoint}:${satOffset}`,
        ...(source.inscriptionId ? { existingInscriptionId: source.inscriptionId } : {}),
        parentRelation: source.parentRelation === true,
      } : {}),
    }];
  });
  return selections.length ? selections : undefined;
}

function finalizeTransactionInputs(transaction: Transaction): void {
  for (let index = 0; index < transaction.inputsLength; index += 1) {
    const input = transaction.getInput(index);
    if (!input.finalScriptWitness?.length && !input.finalScriptSig?.length) transaction.finalizeIdx(index);
  }
}

function assertUnchangedTransaction(transaction: Transaction, expected: Transaction, role: string): void {
  if (hex.encode(transaction.unsignedTx) !== hex.encode(expected.unsignedTx)) {
    throw new Error(`The wallet changed the ${role} transaction. No transaction was sent.`);
  }
  for (let index = 0; index < expected.inputsLength; index += 1) {
    const expectedPrevout = expected.getInput(index).witnessUtxo;
    const returnedPrevout = transaction.getInput(index).witnessUtxo;
    if (expectedPrevout && (!returnedPrevout || returnedPrevout.amount !== expectedPrevout.amount
      || hex.encode(returnedPrevout.script) !== hex.encode(expectedPrevout.script))) {
      throw new Error(`The wallet changed the ${role} input value or script. No transaction was sent.`);
    }
  }
}

export function finalizeCommitPsbt(signedPsbt: Uint8Array, expectedCommit?: Transaction): Transaction {
  const transaction = Transaction.fromPSBT(signedPsbt);
  if (expectedCommit) assertUnchangedTransaction(transaction, expectedCommit, "payment");
  finalizeTransactionInputs(transaction);
  return transaction;
}

export function buildRevealTransaction(
  plan: InscriptionPlan,
  signedCommitTxid: string,
  network: BTC_NETWORK = NETWORK,
  lockTime = 0,
): Transaction {
  const transaction = new Transaction({
    version: plan.revealOptions.transactionVersion ?? 2,
    lockTime,
    customScripts: CUSTOM_SCRIPTS,
    allowUnknownOutputs: true,
    disableScriptCheck: true,
  });
  const ordinalKey = plan.revealPayment.tapInternalKey;
  if (!ordinalKey) throw new Error("Could not derive ordinal signing key");
  const ordinalPayment = p2tr(ordinalKey, undefined, network);
  plan.parentSources.forEach((parent) => {
    const outpoint = parseOutpoint(parent.outpoint);
    transaction.addInput({
      ...ordinalPayment,
      txid: outpoint.txid,
      index: outpoint.index,
      witnessUtxo: { script: ordinalPayment.script, amount: BigInt(parent.value) },
      sequence: plan.revealOptions.inputSequence ?? 0xfffffffd,
    });
  });
  const addSourceInput = (source: OrdinalSource) => {
    const outpoint = parseOutpoint(source.outpoint);
    transaction.addInput({
      ...ordinalPayment,
      txid: outpoint.txid,
      index: outpoint.index,
      witnessUtxo: { script: ordinalPayment.script, amount: BigInt(source.value) },
      sequence: 0xfffffffd,
    });
  };
  if (plan.alignmentOutputAmount > 0) {
    plan.sourceSources.forEach((source, index) => {
      transaction.addInput({
        txid: signedCommitTxid,
        index,
        witnessUtxo: { script: plan.revealPayment.script, amount: BigInt(plan.revealCommitAmounts[index] ?? DEFAULT_POSTAGE) },
        tapInternalKey: plan.revealPayment.tapInternalKey,
        tapMerkleRoot: plan.revealPayment.tapMerkleRoot,
        sequence: plan.revealOptions.inputSequence ?? 0xfffffffd,
      });
      addSourceInput(source);
    });
  } else {
    plan.sourceSources.forEach(addSourceInput);
  }
  const revealCommitIndex = plan.revealCommitAmounts.length - 1;
  transaction.addInput({
    ...(plan.ephemeralPrivateKey ? {} : plan.revealPayment),
    txid: signedCommitTxid,
    index: revealCommitIndex,
    witnessUtxo: { script: plan.revealPayment.script, amount: BigInt(plan.revealCommitAmounts[revealCommitIndex] ?? plan.revealAmount) },
    sequence: plan.revealOptions.inputSequence ?? 0xfffffffd,
  });
  addRevealOutputs(
    transaction,
    plan.revealOptions,
    plan.parentSources,
    plan.recipients,
    plan.outputPostages,
    ordinalPayment.address ?? plan.recipients[0],
    network,
    plan.alignmentOutputAmount,
  );
  return transaction;
}

export async function mineRevealTransaction(
  plan: InscriptionPlan,
  signedCommitTxid: string,
  network: BTC_NETWORK = NETWORK,
  onProgress?: (attempt: number) => void,
): Promise<Transaction> {
  const beginning = plan.revealOptions.vanityStart?.trim().toLowerCase() ?? "";
  const ending = plan.revealOptions.vanityEnd?.trim().toLowerCase() ?? "";
  if (!beginning && !ending) return buildRevealTransaction(plan, signedCommitTxid, network);
  for (let lockTime = 0; lockTime <= MAX_VANITY_NLOCKTIME; lockTime += 1) {
    const transaction = buildRevealTransaction(plan, signedCommitTxid, network, lockTime);
    const id = transaction.id;
    if ((!beginning || id.startsWith(beginning)) && (!ending || id.endsWith(ending))) return transaction;
    if (lockTime % 250 === 0) {
      onProgress?.(lockTime);
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }
  throw new Error("No matching vanity inscription ID was found in the bounded search");
}

export function finalizeRevealPsbt(signedPsbt: Uint8Array, plan: InscriptionPlan, expectedReveal?: Transaction): Transaction {
  const transaction = Transaction.fromPSBT(signedPsbt, { customScripts: CUSTOM_SCRIPTS, disableScriptCheck: true });
  if (expectedReveal) assertUnchangedTransaction(transaction, expectedReveal, "inscription");
  const index = plan.revealInputIndex;
  if (plan.ephemeralPrivateKey && !transaction.getInput(index).finalScriptWitness?.length) {
    transaction.updateInput(index, {
      tapInternalKey: plan.revealPayment.tapInternalKey,
      tapMerkleRoot: plan.revealPayment.tapMerkleRoot,
      tapLeafScript: plan.revealPayment.tapLeafScript,
    }, true);
    transaction.signIdx(plan.ephemeralPrivateKey, index, undefined, new Uint8Array(32));
    const signedRevealInput = transaction.getInput(index);
    if (signedRevealInput.tapScriptSig?.length && signedRevealInput.tapKeySig) {
      transaction.updateInput(index, { tapKeySig: undefined }, true);
    }
  } else if (transaction.getInput(index).tapKeySig) {
    transaction.updateInput(index, { tapKeySig: undefined }, true);
  }
  finalizeTransactionInputs(transaction);
  validateRevealSignatures(transaction, plan);
  if (transaction.weight > MAX_STANDARD_TX_WEIGHT) throw new Error("Signed reveal transaction exceeds the standard weight limit");
  return transaction;
}

/** PSBT finalization assembles witnesses; it does not verify wallet signatures. */
function validateRevealSignatures(transaction: Transaction, plan: InscriptionPlan): void {
  const leaf = plan.revealPayment.tapLeafScript?.[0];
  if (!leaf || !plan.revealPayment.tapInternalKey) throw new Error("The inscription signing script is missing");
  const script = leaf[1].slice(0, -1);
  const leafVersion = leaf[1][leaf[1].length - 1];
  const controlBlock = TaprootControlBlock.encode(leaf[0]);
  const prevouts = Array.from({ length: transaction.inputsLength }, (_, index) => {
    const prevout = transaction.getInput(index).witnessUtxo;
    if (!prevout) throw new Error("The signed inscription is missing an input value or script");
    return prevout;
  });
  for (let index = 0; index < transaction.inputsLength; index += 1) {
    const input = transaction.getInput(index);
    const witness = input.finalScriptWitness;
    const isReveal = index === plan.revealInputIndex;
    if (input.finalScriptSig?.length || !witness || witness.length !== (isReveal ? 3 : 1)) {
      throw new Error(isReveal
        ? "The wallet did not sign the inscription script. No transaction was sent."
        : "The wallet did not sign a parent or selected-sat input. No transaction was sent.");
    }
    if (isReveal && (hex.encode(witness[1]!) !== hex.encode(script) || hex.encode(witness[2]!) !== hex.encode(controlBlock))) {
      throw new Error("The wallet changed the inscription script. No transaction was sent.");
    }
    const signature = witness[0]!;
    const sighash = signature.length === 64 ? 0 : signature.length === 65 && signature[64] === 1 ? 1 : -1;
    if (sighash < 0) throw new Error("The inscription requires signatures covering every input and output");
    const output = OutScript.decode(prevouts[index]!.script);
    if (output.type !== "tr") throw new Error("Parent and inscription inputs must use Taproot");
    const publicKey = isReveal
      ? plan.ephemeralPrivateKey ? pubSchnorr(plan.ephemeralPrivateKey) : plan.revealPayment.tapInternalKey
      : output.pubkey;
    const message = transaction.preimageWitnessV1(index, prevouts.map((prevout) => prevout.script), sighash,
      prevouts.map((prevout) => prevout.amount), undefined, isReveal ? script : undefined, isReveal ? leafVersion : undefined);
    if (!schnorr.verify(signature.slice(0, 64), message, publicKey)) {
      throw new Error(isReveal
        ? "The wallet returned an invalid inscription signature. No transaction was sent."
        : "The wallet returned an invalid parent or selected-sat signature. No transaction was sent.");
    }
  }
}

export function createDeterministicTestWallet(network: BTC_NETWORK): ConnectedWallet {
  const paymentPrivateKey = hex.decode("11".repeat(32));
  const ordinalsPrivateKey = hex.decode("22".repeat(32));
  const paymentPublicKey = pubECDSA(paymentPrivateKey);
  const ordinalsPublicKey = pubSchnorr(ordinalsPrivateKey);
  const payment = p2wpkh(paymentPublicKey, network);
  const ordinals = p2tr(ordinalsPublicKey, undefined, network);
  if (!payment.address || !ordinals.address) throw new Error("Failed to create deterministic wallet");
  return {
    provider: "xverse",
    payment: { address: payment.address, publicKey: hex.encode(paymentPublicKey), purpose: "payment", addressType: "p2wpkh" },
    ordinals: { address: ordinals.address, publicKey: hex.encode(ordinalsPublicKey), purpose: "ordinals", addressType: "p2tr" },
    ownershipVerified: true,
  };
}
